import React, { useState, useEffect, useRef } from "react";
import ChatMessage from "./ChatMessage";
import ChatService from "../services/ChatAPIService";
import ContactInfo from "./ContactInfo";
import ModeSwitch from "./ModeSwitch";
import VoiceStage from "./VoiceStage";
import "./ChatWindow.css";
import {
  ArrowUp,
  Briefcase,
  Cpu,
  Hammer,
  Lightbulb,
  Moon,
  Plus,
  Sun,
} from "lucide-react";
import { useTheme } from "../hooks/useTheme";
import {
  avatarFor,
  statusFor,
  nextThinkingAvatar,
  THINK_TIMEOUT_MS,
  THINK_CYCLE_MS,
} from "../utils/vaiAvatar";

const INITIAL_MESSAGES = [];

const SUGGESTIONS = [
  { text: "Where has Vaibhav worked?", icon: Briefcase },
  { text: "How does he think about products?", icon: Lightbulb },
  { text: "What does he believe about engineering?", icon: Cpu },
  { text: "What's he building outside work?", icon: Hammer },
];

const ChatWindow = () => {
  const { theme, toggle } = useTheme();
  const [messages, setMessages] = useState(INITIAL_MESSAGES);

  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [mode, setMode] = useState("chat");
  const [isInfoOpen, setIsInfoOpen] = useState(() => {
    return window.location.pathname.toLowerCase() !== "/vai";
  });
  const [clickedExamples, setClickedExamples] = useState([]);
  const maxMessages = 4;
  const isEmpty = messages.length === 0 && clickedExamples.length === 0;

  const endOfMessagesRef = useRef(null);
  const chatWindowRef = useRef(null);
  // Stick to the bottom only while the user is already there; a deliberate
  // scroll-up pauses auto-scroll until they return to the bottom.
  const stickToBottomRef = useRef(true);
  // vAI avatar state: idle | thinking | typing | speaking. While a reply is
  // slow, a timeout cycles thinking <-> typing so the avatar keeps working.
  const [avatarState, setAvatarState] = useState("idle");
  const thinkTimerRef = useRef(null);

  const clearThinkTimer = () => {
    if (thinkTimerRef.current) {
      clearTimeout(thinkTimerRef.current);
      thinkTimerRef.current = null;
    }
  };

  // Never leave a dangling timer behind on unmount.
  useEffect(() => clearThinkTimer, []);

  const startThinkCycle = () => {
    clearThinkTimer();
    const cycle = () => {
      setAvatarState((prev) => nextThinkingAvatar(prev));
      thinkTimerRef.current = setTimeout(cycle, THINK_CYCLE_MS);
    };
    thinkTimerRef.current = setTimeout(cycle, THINK_TIMEOUT_MS);
  };

  useEffect(() => {
    const updateHeight = () => {
      // On mobile the chat is pinned to the viewport in CSS (100dvh). A
      // measured pixel height would fight the dynamic viewport when
      // Safari's URL bar shows or hides, so leave it alone there.
      const smallScreen =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(max-width: 560px)").matches;
      if (smallScreen) {
        if (chatWindowRef.current) chatWindowRef.current.style.height = "";
        return;
      }
      if (chatWindowRef.current) {
        chatWindowRef.current.style.height = `${window.innerHeight}px`;
      }
    };

    // Initial height set
    updateHeight();

    // Update height on resize
    window.addEventListener("resize", updateHeight);

    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  useEffect(() => {
    if (stickToBottomRef.current && endOfMessagesRef.current) {
      const reduceMotion =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      endOfMessagesRef.current.scrollIntoView({
        behavior: reduceMotion ? "auto" : "smooth",
      });
    }
  }, [messages]);

  const handleMessagesScroll = (e) => {
    const el = e.currentTarget;
    stickToBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const sendMessage = async (messageText) => {
    if (messageText.trim()) {
      setMessages([...messages, { sender: "user", text: messageText }]);
      setInput("");
      setAvatarState("thinking");
      startThinkCycle();

      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: "bot", text: "..." },
      ]);

      try {
        const lastMessages = messages.slice(-maxMessages);
        let spoke = false;
        await ChatService.streamChatResponse(
          messageText,
          lastMessages,
          (token) => {
            if (!spoke) {
              // First token: the reply has started — stop cycling, start speaking.
              spoke = true;
              clearThinkTimer();
              setAvatarState("speaking");
            }
            setMessages((prevMessages) => {
              const updated = [...prevMessages];
              const last = updated[updated.length - 1];
              updated[updated.length - 1] = {
                ...last,
                text: (last.text === "..." ? "" : last.text) + token,
              };
              return updated;
            });
          }
        );
        clearThinkTimer();
        setAvatarState("idle");
      } catch (error) {
        clearThinkTimer();
        setAvatarState("idle");
        setError("Error occurred while fetching response");
        setTimeout(() => {
          setError("");
          setMessages((prevMessages) => {
            const newMessages = prevMessages.slice(0, -1);
            return [...newMessages];
          });
        }, 1000);
      }
    }
  };

  const handleSend = () => {
    sendMessage(input);
  };

  const handleSuggestionClick = (message) => {
    setClickedExamples([...clickedExamples, message]);
    sendMessage(message);
  };

  const startNewChat = () => {
    setMessages(INITIAL_MESSAGES);
    setClickedExamples([]);
    setInput("");
    setError("");
    clearThinkTimer();
    setAvatarState("idle");
  };

  const toggleInfo = () => {
    setIsInfoOpen(!isInfoOpen);
  };

  const handleVoiceExit = ({ userText, vaiText }) => {
    setMode("chat");
    setMessages((prev) => [
      ...prev,
      ...(userText ? [{ sender: "user", text: userText }] : []),
      ...(vaiText ? [{ sender: "bot", text: vaiText }] : []),
    ]);
  };

  return (
    <div
      className={`chat-window${isInfoOpen ? "" : " is-chat"}`}
      ref={chatWindowRef}
    >
      {isInfoOpen ? (
        <ContactInfo isOpen={isInfoOpen} onClose={toggleInfo} />
      ) : (
        <div className="chat-content">
          <div
            className={`vai-float${avatarState === "idle" ? "" : " working"}`}
            aria-hidden="true"
          >
            <img
              key={avatarState}
              src={avatarFor(avatarState)}
              alt=""
              className="vai-float-img"
            />
            <div className="vai-float-pill">
              <b>vAI</b>
              <span>{statusFor(avatarState)}</span>
            </div>
          </div>
          {mode === "voice" && <VoiceStage onExit={handleVoiceExit} />}
          <div className="chat-header">
            <div className="brand">
              <span className="brand-name">vAI</span>
              <span className="brand-sub">Vaibhav&rsquo;s personal AI</span>
            </div>
            <div className="header-actions">
              <button
                className="header-button"
                onClick={(e) => {
                  startNewChat();
                  // Mouse users: drop focus so the ring never sticks.
                  // Keyboard users (e.detail === 0) keep it.
                  if (e.detail > 0) e.currentTarget.blur();
                }}
                onMouseDown={(e) => e.preventDefault()}
                aria-label="Start a new chat"
              >
                <Plus size={20} />
              </button>
              <button
                className="header-button"
                onClick={(e) => {
                  toggle();
                  if (e.detail > 0) e.currentTarget.blur();
                }}
                onMouseDown={(e) => e.preventDefault()}
                aria-label="Toggle color theme"
              >
                {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
              </button>
            </div>
          </div>
          <div
            className={`messages ${isEmpty ? "empty" : ""}`}
            onScroll={handleMessagesScroll}
          >
            {isEmpty ? (
              <div className="welcome">
                <h1 className="welcome-title">Hey, I&rsquo;m vAI.</h1>
                <p className="welcome-text">
                  Vaibhav&rsquo;s AI alter ego. I know a bit about his work,
                  projects, opinions, and whatever else he&rsquo;s shared
                  with me. Ask away. I&rsquo;ll do my best to sound like him.
                </p>
                <div className="suggestion-grid">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s.text}
                      className="suggestion-card"
                      onClick={() => handleSuggestionClick(s.text)}
                    >
                      <s.icon size={16} className="suggestion-icon" />
                      <span>{s.text}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="conversation">
                {messages.map((msg, index) => (
                  <ChatMessage
                    key={index}
                    sender={msg.sender}
                    text={msg.text}
                  />
                ))}
              </div>
            )}
            <div ref={endOfMessagesRef} />
          </div>
          {error && <div className="error-message">{error}</div>}
          <div className="composer-zone">
            <div className="mode-switch-row">
              <ModeSwitch mode={mode} onChange={setMode} />
            </div>
            <div className="composer">
              <input
                type="text"
                placeholder="Ask me anything..."
                aria-label="Message vAI"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
              />
              <button
                className="send-button"
                onClick={handleSend}
                disabled={!input.trim()}
                aria-label="Send message"
              >
                <ArrowUp size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWindow;
